import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import type { AppDaMetaDaPlataforma } from '@/data/tipos';

const TEXTO = { margin: 0, fontSize: 12.5, lineHeight: '18px', color: 'var(--text-secondary)' } as const;

/** O endereço de volta que se cadastra no app da Meta: a borda `meta-ads`, no domínio da API. */
export function retornoDaMeta(api: string | undefined = import.meta.env.VITE_SUPABASE_URL as string | undefined): string {
    return `${(api ?? '').replace(/\/+$/, '')}/functions/v1/meta-ads/callback`;
}

/**
 * O app da Meta da plataforma (0162), no painel do super admin.
 *
 * É da plataforma pela mesma razão das chaves de IA: cada clínica entra com a
 * própria conta da Meta, mas pelo app da Fórmula MED. A chave secreta entra e não
 * volta — o que se lê são os quatro últimos caracteres.
 */
export function AppDaMeta({ app }: { app: AppDaMetaDaPlataforma | null }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const [aberto, setAberto] = useState(false);
    const [appId, setAppId] = useState('');
    const [segredo, setSegredo] = useState('');
    const [configId, setConfigId] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const sujo = useAlteracaoPendente({ appId, segredo, configId }, aberto);

    const abrir = () => {
        setAppId(app?.appId ?? '');
        setConfigId(app?.configId ?? '');
        setSegredo('');
        setErro(null);
        setAberto(true);
    };

    const salvar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            await dados().salvarAppDaMeta(appId, segredo, configId);
            aviso({ title: 'App da Meta guardado', description: segredo ? 'Chave secreta cifrada no cofre' : 'A chave guardada continua a mesma' });
            setAberto(false);
            setSegredo('');
            revalidator.revalidate();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível guardar.');
        } finally {
            setSalvando(false);
        }
    };

    const remover = async () => {
        const ok = await confirmar({
            titulo: 'Remover o app da Meta?',
            descricao: 'As clínicas deixam de conseguir conectar e a leitura diária dos anúncios para. As contas já conectadas ficam guardadas, esperando o app voltar.',
            confirmar: 'Remover app',
        });
        if (!ok) return;
        try {
            await dados().removerAppDaMeta();
            aviso({ title: 'App da Meta removido' });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível remover', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const podeSalvar = /^[0-9]{5,20}$/.test(appId.trim()) && (segredo.trim() !== '' || Boolean(app)) && !salvando;

    return (
        <Card
            title="Meta Ads (Facebook e Instagram)"
            subtitle="O app pelo qual cada clínica conecta a própria conta de anúncios"
            padding={18}
            actions={<Badge tone={app ? 'success' : 'neutral'} dot>{app ? 'Configurado' : 'Não configurado'}</Badge>}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {app ? (
                    <p style={TEXTO}>
                        App <strong>{app.appId}</strong> · chave terminando em <strong>{app.dica ?? '····'}</strong>
                        {app.configId ? ` · Login para Empresas ${app.configId}` : ' · permissões por escopo (ads_read, business_management)'}
                        {app.atualizadoEm ? ` · atualizado em ${dataEHoraPorExtenso(app.atualizadoEm)}` : ''}
                    </p>
                ) : (
                    <p style={TEXTO}>Sem o app, o botão "Conectar Meta Ads" das clínicas não aparece.</p>
                )}
                <p style={TEXTO}>
                    Endereço de retorno para cadastrar no app (Login do Facebook › URIs de redirecionamento):{' '}
                    {retornoDaMeta().startsWith('http')
                        ? <code style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{retornoDaMeta()}</code>
                        : 'aparece quando a aplicação está ligada ao servidor.'}
                </p>
                <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button size="sm" variant="secondary" onClick={abrir}>{app ? 'Trocar' : 'Configurar'}</Button>
                    {app && <Button size="sm" variant="ghost" onClick={() => { void remover(); }}>Remover</Button>}
                </span>
            </div>

            <Modal
                open={aberto} width={560} title="App da Meta" onClose={() => setAberto(false)} dirty={sujo}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setAberto(false)}>Cancelar</Button>
                        <Button onClick={() => { void salvar(); }} disabled={!podeSalvar}>{salvando ? 'Guardando…' : 'Guardar app'}</Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                    <FormField label="ID do app" htmlFor="meta-app-id" required hint="Em developers.facebook.com › seu app › Configurações › Básico.">
                        <Input id="meta-app-id" value={appId} onChange={setAppId} inputMode="numeric" autoComplete="off" />
                    </FormField>
                    <FormField
                        label="Chave secreta do app" htmlFor="meta-app-segredo" required={!app}
                        hint={app ? 'Deixe em branco para manter a chave guardada.' : '32 caracteres. Fica cifrada no cofre; o navegador não a recebe de volta.'}
                    >
                        <Input id="meta-app-segredo" type="password" autoComplete="off" value={segredo} onChange={setSegredo} />
                    </FormField>
                    <FormField
                        label="ID da configuração (Login para Empresas)" htmlFor="meta-app-config"
                        hint="Opcional. Com ele, a Meta usa as permissões da configuração no lugar dos escopos."
                    >
                        <Input id="meta-app-config" value={configId} onChange={setConfigId} inputMode="numeric" autoComplete="off" />
                    </FormField>
                    <p style={TEXTO}>
                        Até a Meta aprovar o app na Análise (permissão <strong>ads_read</strong>, com a empresa verificada), só quem
                        tem papel no app — administrador, desenvolvedor ou testador — consegue conectar.
                    </p>
                </div>
            </Modal>
            {confirmacao}
        </Card>
    );
}
